import type { RegionEstimate } from './cognitiveEngine';
import { knowledgeFraction } from './cognitiveEngine';

/**
 * Geometry and colour for the Atlas stone, with no rendering in it.
 *
 * Both the on-screen SVG (components/AtlasGem.tsx) and the shareable PNG
 * (lib/shareCard.ts) read from here. The share card previously drew a totally
 * different five-hue ring chart, so a player's shared image did not match the
 * app it came from. One model, two renderers, no drift.
 *
 * Everything is expressed in a 240x240 box centred on (120, 120). Renderers
 * scale it themselves.
 */

export type RGB = [number, number, number];
export type P = [number, number];

// Ramp anchors. Lightness rises monotonically across all four, which is what
// makes this a legal sequential scale rather than a decorative gradient. Two
// hues, violet to teal, in the viridis family, so it survives colour-vision
// deficiency. Five categorical hues were tried first and failed validation.
// Stops are POSITIONED, not evenly spaced. Interpolating violet to teal in RGB
// passes through blue, and evenly spaced anchors parked the middle of the range
// squarely in it, so ordinary regions came out sky blue. Violet now owns most of
// the scale and teal arrives late and fast, which is both what the reference
// looks like and what makes a strong region read as an event.
// The bridge sits high and narrow on purpose. A typical strong region lands
// around 0.75 to 0.85 on this scale, so a wider bridge painted every competent
// player's whole stone sky blue. Violet now holds until 0.86 and teal only
// arrives near the ceiling, which makes a commanding region read as an event
// instead of as the default.
const RAMP: Array<{ at: number; rgb: RGB }> = [
  { at: 0.0, rgb: [0x33, 0x21, 0x5c] },  // deep violet, barely lit
  { at: 0.45, rgb: [0x6b, 0x3f, 0xc4] },
  { at: 0.86, rgb: [0x9b, 0x6f, 0xf0] }, // brand violet, holds most of the scale
  { at: 0.94, rgb: [0x6f, 0xc4, 0xd8] }, // tight cyan bridge
  { at: 1.0, rgb: [0x3a, 0xd6, 0xc0] },  // teal, brightest and rare
];

// An unlit facet is deep violet with structure, not black. Black facets punched
// holes in the stone and made the shading read as damage rather than depth.
export const UNLIT: RGB = [0x2e, 0x27, 0x47];

export function rampRGB(t: number): RGB {
  const x = Math.max(0, Math.min(1, t));
  let i = 0;
  while (i < RAMP.length - 2 && x > RAMP[i + 1].at) i++;
  const a = RAMP[i];
  const b = RAMP[i + 1];
  const f = (x - a.at) / (b.at - a.at);
  return [0, 1, 2].map(k => a.rgb[k] + (b.rgb[k] - a.rgb[k]) * f) as RGB;
}

export const css = ([r, g, b]: RGB) => `rgb(${Math.round(r)},${Math.round(g)},${Math.round(b)})`;
export const shade = (c: RGB, f: number): RGB =>
  c.map(v => Math.max(0, Math.min(255, v * f))) as RGB;

/** The bars in Profile use this so a bar and its facets agree exactly. */
export function rampColor(t: number): string {
  return css(rampRGB(t));
}

/** Polar helper. Angle 0 points up, degrees, clockwise. */
function pt(r: number, deg: number): P {
  const a = ((deg - 90) * Math.PI) / 180;
  return [120 + r * Math.cos(a), 120 + r * Math.sin(a)];
}

// Fixed irregularity so the silhouette reads as a cut stone rather than a
// geometric decagon. Deterministic, never random: the same player must get the
// same stone on every render, in the app and in the shared image alike.
const JIT_OUT = [1.0, 0.93, 0.99, 0.88, 1.0, 0.92, 0.97, 0.9, 1.0, 0.94];
const JIT_IN = [0.96, 1.06, 0.92, 1.03, 0.98, 1.07, 0.94, 1.02, 0.97, 1.05];
const R_OUT = 97;
// The table is a focal facet, not the whole stone. Larger than this and the
// result reads as a medallion.
const R_IN = 41;

export const OUT: P[] = JIT_OUT.map((j, k) => pt(R_OUT * j, k * 36));
export const IN: P[] = JIT_IN.map((j, k) => pt(R_IN * j, k * 36 + 18));

/**
 * Crown: twenty triangles between the outer and inner rings, which are offset
 * by half a step so the two rings interlock. No triangle touches the centre,
 * and the inner ring closes as one table facet. An earlier version fanned every
 * facet from the middle, which turned the stone into a pie chart with a bevel.
 */
export const CROWN: P[][] = [];
for (let k = 0; k < 10; k++) {
  CROWN.push([OUT[k], OUT[(k + 1) % 10], IN[k]]);
  CROWN.push([IN[(k + 9) % 10], OUT[k], IN[k]]);
}
export const TABLE: P[] = IN;

/**
 * Regions are not assigned to facets. Handing each region its own facets forces
 * a choice between two bad pictures: contiguous facets make a pie chart, and
 * scattered facets make confetti. Instead each region is an anchor direction and
 * every facet takes a weighted blend of all five by angular proximity, so light
 * pools and fades across the stone the way it does in a real one.
 */
const REGION_ANGLE = [0, 72, 144, 216, 288];
/** How tightly light stays near its anchor. Higher is more localised. */
const FALLOFF = 2.4;

function blendWeights(deg: number): number[] {
  const w = REGION_ANGLE.map(a => {
    const dd = ((((deg - a) % 360) + 540) % 360) - 180; // signed delta, -180..180
    return Math.pow(Math.max(0, Math.cos((dd * Math.PI) / 180)), FALLOFF);
  });
  const sum = w.reduce((s, v) => s + v, 0);
  // Directly opposite every anchor the cosines all vanish; fall back to an even
  // share so those facets stay lit instead of dropping out.
  return sum > 1e-6 ? w.map(v => v / sum) : w.map(() => 0.2);
}

const centroid = (f: P[]): P => [
  f.reduce((s, p) => s + p[0], 0) / f.length,
  f.reduce((s, p) => s + p[1], 0) / f.length,
];

/** Angle of a facet's centroid, in the same frame as REGION_ANGLE. */
function facetAngle(f: P[]): number {
  const [x, y] = centroid(f);
  return ((((Math.atan2(y - 120, x - 120) * 180) / Math.PI + 90) % 360) + 360) % 360;
}

/**
 * Directional light from the upper left, so the stone reads as a solid.
 * Deliberately narrow: measured in OKLab, the old 0.76..1.12 span put more
 * colour difference between two facets of the SAME region (dE 15.9) than the
 * data put between Steady and Commanding (dE 14.7), so the lighting was louder
 * than the information. Volume is a garnish here, never the signal.
 */
function lightFactor(f: P[]): number {
  const [x, y] = centroid(f);
  const cx = x - 120;
  const cy = y - 120;
  const len = Math.hypot(cx, cy) || 1;
  const dot = (cx / len) * -0.6 + (cy / len) * -0.8;
  return 0.9 + 0.16 * (dot * 0.5 + 0.5);
}

/**
 * Stone identities. This is the redesign that followed a measured failure: the
 * first system encoded every region's Elo into facet brightness on one ramp,
 * and in OKLab the lighting noise (15.9 dE) exceeded the data signal
 * (Steady to Commanding, 14.7 dE). Five continuous values cannot live in the
 * brightness of one jewel; the bars already do that job properly.
 *
 * So the stone answers ONE question a glance can actually answer: what kind of
 * player is this. The dominant region names the stone and sets its body colour,
 * the runner-up glows in its own corner, and overall strength drives how
 * brightly the whole thing burns. Which is also exactly the reference image:
 * violet body, teal corner, dark edges.
 */
export const STONES: Record<string, { name: string; hue: RGB }> = {
  SCIENCES:   { name: 'AQUAMARINE', hue: [0x2d, 0xd4, 0xbf] },
  HUMANITIES: { name: 'AMBER',      hue: [0xf5, 0x9e, 0x0b] },
  CULTURE:    { name: 'AMETHYST',   hue: [0xa8, 0x55, 0xf7] },
  LIFESTYLE:  { name: 'GARNET',     hue: [0xf4, 0x3f, 0x5e] },
  ABSTRACT:   { name: 'SAPPHIRE',   hue: [0x5e, 0x9e, 0xf7] },
};

/**
 * No region clearly ahead is its own identity, not a coin flip between two
 * near-equal hues. Nacreous, off-white, unmistakably not one of the five.
 */
export const OPAL: { name: string; hue: RGB } = { name: 'OPAL', hue: [0xcf, 0xc9, 0xe6] };

/** Dominance margin (in knowledgeFraction units) below which the stone is OPAL. */
const OPAL_MARGIN = 0.06;

const mix = (a: RGB, b: RGB, t: number): RGB =>
  a.map((v, k) => v + (b[k] - v) * t) as RGB;

export interface GemIdentity {
  /** Region key of the dominant region, e.g. "HUMANITIES"; "BALANCED" for opal. */
  region: string;
  /** Player-facing stone name, e.g. "AMBER". */
  stone: string;
  /** Runner-up's stone name, if one glows in the corner. */
  secondary: string | null;
}

export interface GemShading {
  /** One colour per CROWN facet, already lit. */
  crown: RGB[];
  table: RGB;
  anyCharted: boolean;
  /**
   * Mean facet brightness, 0 to 1. Renderers scale the interior grain by this.
   * The grain is screened on, so a fixed amount of it lifts a dark stone far
   * more than a bright one, and a weak map ended up looking the same as a strong
   * one. More light inside the stone, more visible structure.
   */
  lum: number;
  /** What the stone IS. Null until something is charted. */
  identity: GemIdentity | null;
}

/**
 * THE GEOMETRY IS FIXED and only the light moves. Nothing about the silhouette,
 * the facet layout or the size responds to the data, so a brand new account
 * renders the same stone, unlit, instead of collapsing to a speck.
 *
 * A HIDDEN region renders dark because it genuinely is unknown, never because
 * it is "bad".
 */
export function gemShading(regions: RegionEstimate[]): GemShading {
  const byIndex = regions.slice(0, 5);

  // Every region reduces to two numbers: where it sits on the knowledge scale,
  // and how much is actually known about it.
  const value = REGION_ANGLE.map((_, i) =>
    byIndex[i] ? knowledgeFraction(byIndex[i].elo) : 0
  );
  const known = REGION_ANGLE.map((_, i) => {
    const t = byIndex[i]?.tier;
    return t === 'CHARTED' ? 1 : t === 'FAINT' ? 0.5 : 0;
  });

  // Rank the observed regions. Ties resolve by canonical index, so the same
  // player always gets the same stone.
  const ranked = [0, 1, 2, 3, 4]
    .filter(i => known[i] > 0 && byIndex[i])
    .sort((a, b) => value[b] - value[a] || a - b);
  const dom = ranked.length > 0 ? ranked[0] : null;
  const sec = ranked.length > 1 ? ranked[1] : null;

  const isOpal = dom !== null && sec !== null && value[dom] - value[sec] < OPAL_MARGIN;
  const domStone =
    dom === null ? null : isOpal ? OPAL : STONES[byIndex[dom].region] ?? OPAL;
  const secStone =
    sec === null || isOpal ? null : STONES[byIndex[sec].region] ?? null;

  /**
   * Hue: the dominant stone's, everywhere, bending toward the runner-up only in
   * the runner-up's own corner. Never a five-way blend: that was the mud.
   *
   * Energy: how known and how strong this direction is. A charted-but-weak
   * region reads as dimly lit, an uncharted one as dark, a commanding one burns.
   */
  const colourAt = (w: number[]): RGB => {
    if (domStone === null) return UNLIT;
    let hue = domStone.hue;
    if (secStone !== null && sec !== null) {
      hue = mix(hue, secStone.hue, Math.min(1, w[sec] * 1.6));
    }
    const raw = w.reduce((s, wi, i) => s + wi * known[i], 0);
    const v = raw > 1e-6 ? w.reduce((s, wi, i) => s + wi * known[i] * value[i], 0) / raw : 0;
    const energy = Math.min(1, raw) * (0.22 + 0.78 * v);
    return mix(UNLIT, hue, energy);
  };

  // The table sits at the centre and leans toward the dominant region: the
  // middle of a stone is where the light collects.
  const rawW = REGION_ANGLE.map((_, i) => (i === dom ? 1.9 : 0.775));
  const wSum = rawW.reduce((s, v) => s + v, 0);
  const tableW = rawW.map(v => v / wSum);

  const crown = CROWN.map(f => shade(colourAt(blendWeights(facetAngle(f))), lightFactor(f)));
  const lum = Math.max(
    0,
    Math.min(1, crown.reduce((s, c) => s + (c[0] + c[1] + c[2]) / 3, 0) / crown.length / 200)
  );

  return {
    crown,
    table: colourAt(tableW),
    anyCharted: byIndex.some(r => r.tier !== 'HIDDEN'),
    lum,
    identity:
      dom === null || domStone === null
        ? null
        : {
            region: isOpal ? 'BALANCED' : byIndex[dom].region,
            stone: domStone.name,
            secondary: secStone?.name ?? null,
          },
  };
}
